/**
 * Tracking Plugin for Native Interactive Site Map (ISM)
 *
 * Detects map zoom/pan/map-type interaction, lot status pill selection, mobile pill horizontal
 * scroll, lot selection on the map, switching either the Floor Plan Detail view's or the Housing
 * Unit (QMI/Model) Detail view's own tabs (Lot & elevation / Floor plan configuration — the
 * latter is "the static floor plan view" the events below are named for), and the mobile "View
 * floor plan" click, and emits each as a raw payload on the matching module-level Subject in
 * redux/observables.js. Has no
 * analytics knowledge of its own — never imports
 * segmentTrack/googleAnalytics4Event/getAnalyticsTrackingProperties and never dispatches an
 * analytics action. Whichever page embeds this feature owns the actual dispatch — its own
 * tracking files subscribe to those Subjects — imported via this feature's index.js, along with
 * the buildEvent functions in services/segmentEventBuilder.js and
 * services/googleAnalytics4EventBuilder.js — to turn each payload into an actual dispatch. See
 * redux/observables.js and TRACKING_PLUGIN_SEQUENCE.md for the full flow.
 *
 * Pure orchestrator otherwise — wires handlers/mapInteractionHandlers.js,
 * handlers/scrollHandlers.js (bound twice: chip-pill bar and card grid),
 * handlers/getNotifiedInteractionHandlers.js, handlers/delegatedClickHandlers.js (bound three
 * times: Similar Homes toggle, similar-home card, and mobile Sold "See All" and
 * handlers/floorPlanDetailInteractionHandlers.js, plus Redux store subscriptions. Never touches CSS;
 * the only DOM it reads is the map element, the chips wrapper, the listing content, the panel, and
 * the shared drawer element, each handed to its matching handler above.
 *
 * @module nativeInteractiveSiteMap/plugins/tracking.plugin
 */
import { Subject, fromEvent } from 'rxjs';
import { 
  debounceTime, 
  distinctUntilChanged, 
  filter, 
  map, 
  pairwise, 
  take, 
  throttleTime 
} from 'rxjs/operators';
import createMapInteractionHandlers from '../handlers/mapInteractionHandlers';
import createScrollHandlers from '../handlers/scrollHandlers';
import createDrawerDragHandlers from '../handlers/drawerDragHandlers';
import createDrawerOpenHandlers from '../handlers/drawerOpenHandlers';
import createGetNotifiedInteractionHandlers from '../handlers/getNotifiedInteractionHandlers';
import createFloorPlanDetailInteractionHandlers from '../handlers/floorPlanDetailInteractionHandlers';
import createDelegatedClickHandlers from '../handlers/delegatedClickHandlers';
import { isTrackedFilterField } from '../services/filterTrackingValues';
import {
  selectActiveChip,
  selectChipLotCounts,
  selectSelectedLotId,
  selectSelectedTab,
  selectHousePlanCount,
  selectHousingUnitCount,
  selectLots,
  selectFilteredHousePlans,
  selectViewEntrySource,
  selectCurrentlySelectedLot,
  selectCurrentlySelectedHousingUnitLot,
  selectActiveFloorPlanId,
  selectActiveHousingUnitId,
  selectActiveView,
  selectActiveFloorPlan,
  selectActiveHousingUnit,
  selectFilterDraft,
  selectFilterDraftActiveFilterCount,
  selectFilterDraftHasPendingChanges,
  selectFilterDraftResultCount,
} from '../redux/selectors';
import {
  mapInteractionOccurred$,
  panelUpdatedOccurred$,
  panelScrollOccurred$,
  drawerDraggedOccurred$,
  lotSelectedOccurred$,
  cardsScrolledOccurred$,
  tabSelectedOccurred$,
  notifyWhenAvailableClickedOccurred$,
  floorPlanViewedOccurred$,
  viewFloorPlanClickedOccurred$,
  emptyConfigViewedOccurred$,
  similarHomesToggledOccurred$,
  similarHomeCardClickedOccurred$,
  soldSeeAllClickedOccurred$,
  detailViewOpenedOccurred$,
  detailTabClicked$,
  detailsTabSelectedOccurred$,
  changeLotSelectedOccurred$,
  exteriorSelectedOccurred$,
  exteriorSelectorExpandedOccurred$,
  mobileLotMapClickedOccurred$,
  mobileLotCardNavigationOccurred$,
  filtersModalAction$,
  filterPanelOpenedOccurred$,
  filterPanelClosedOccurred$,
  filterUpdatedOccurred$,
  filtersAppliedOccurred$,
  filtersClearedOccurred$,
} from '../redux/observables';
import {
  TAB_QUICK_MOVE_INS,
  TAB_FLOOR_PLAN_CONFIG,
  VIEW_FLOOR_PLAN_DETAIL,
  VIEW_HOUSING_UNIT_DETAIL,
  VIEW_MOBILE_LOT_CARD
} from '../constants';

/**
 * Parses activeViewContext.planId (selectActiveFloorPlanId's raw value — string or number,
 * however it was last written) the same way selectActiveFloorPlan does internally before its
 * housePlans lookup. Used as a fallback below so a known active plan id still gets reported even
 * when the matching plan record hasn't landed in housePlans yet (e.g. a race on entry) — the
 * Subjects' payloads declare `planId: number`, not `number|null`, for exactly this case.
 * @param {object} state
 * @returns {number|null}
 */
function resolveActiveFloorPlanId(state) {
  const rawPlanId = selectActiveFloorPlanId(state);
  if (rawPlanId == null) {
    return null;
  }
  const numericPlanId = typeof rawPlanId === 'string' ? parseInt(rawPlanId, 10) : rawPlanId;
  return Number.isNaN(numericPlanId) ? null : numericPlanId;
}

/**
 * plan_id/spec_id/lot_id shared by all four "static floor plan" tracking events, covering both
 * views that render them: the plan-backed Floor Plan Detail view (VIEW_FLOOR_PLAN_DETAIL) and the
 * spec-backed Housing Unit (QMI/Model) Detail view (VIEW_HOUSING_UNIT_DETAIL) — these events use
 * the same names/properties for both views, distinguished only by which of planId/specId is
 * populated. Falls back to all-null ids when neither view is active.
 * @param {object} state
 * @returns {{ planId: number|null, specId: number|null, lotId: number|null }}
 */
function getStaticFloorPlanTrackingIds(state) {
  const activeView = selectActiveView(state);

  if (activeView === VIEW_HOUSING_UNIT_DETAIL) {
    const activeHousingUnit = selectActiveHousingUnit(state);
    const lot = selectCurrentlySelectedHousingUnitLot(state);

    return {
      planId: null,
      specId: activeHousingUnit?.specificationId ?? selectActiveHousingUnitId(state),
      lotId: lot?.lotId ?? null,
    };
  }

  if (activeView === VIEW_FLOOR_PLAN_DETAIL) {
    const activeFloorPlan = selectActiveFloorPlan(state);
    const lot = selectCurrentlySelectedLot(state);

    return {
      planId: activeFloorPlan?.planId ?? resolveActiveFloorPlanId(state),
      specId: lot?.housingUnitId ?? null,
      lotId: lot?.lotId ?? null,
    };
  }

  return { planId: null, specId: null, lotId: null };
}

const ZOOM_DEBOUNCE_MS = 300;

// A single drag-to-open gesture trips both the drag handler (pointer movement) and the open
// observer (collapsed→open); throttling the merged stream collapses that into one emission, while
// a plain tap-to-open (open observer only) still emits once.
const DRAWER_INTERACTION_THROTTLE_MS = 500;

/**
 * Number of cards rendered in the panel's active tab (the `card_count` tracking property). Only
 * the Quick Move-Ins tab shows housing-unit cards, and it exists only for the Available chip
 * (services/listViewContentService.js is the source of truth for which chips have tabs) — every
 * other view renders the floor-plan grid — so keying off the selected tab alone is sufficient
 * without re-deriving hasTabs here.
 *
 * @param {object} state
 * @returns {number}
 */
function getActivePanelCardCount(state) {
  return selectSelectedTab(state) === TAB_QUICK_MOVE_INS
    ? selectHousingUnitCount(state)
    : selectHousePlanCount(state);
}

/**
 * @param {string} activeView
 * @returns {boolean} Whether activeView is one of the two detail views (floor plan or housing unit).
 */
function isDetailView(activeView) {
  return activeView === VIEW_FLOOR_PLAN_DETAIL || activeView === VIEW_HOUSING_UNIT_DETAIL;
}

/**
 * @param {object} storeState
 * @returns {object|null} The relevant lot for the active detail view — selectCurrentlySelectedLot
 * only works for floorPlanDetail (it keys off planId, which is always null in housingUnitDetail).
 */
function selectCurrentDetailLot(storeState) {
  return selectActiveView(storeState) === VIEW_HOUSING_UNIT_DETAIL
    ? selectCurrentlySelectedHousingUnitLot(storeState)
    : selectCurrentlySelectedLot(storeState);
}

function getTrackingMapType(mapType) {
  return mapType === 'roadmap' ? 'road' : 'satellite';
}

export default function createTrackingPlugin() {
  const state = {
    mapInteractionHandlers: createMapInteractionHandlers(),
    chipScrollHandlers: createScrollHandlers(),
    cardsScrollHandlers: createScrollHandlers(),
    drawerDragHandlers: createDrawerDragHandlers(),
    drawerOpenHandlers: createDrawerOpenHandlers(),
    getNotifiedInteractionHandlers: createGetNotifiedInteractionHandlers(),
    floorPlanDetailInteractionHandlers: createFloorPlanDetailInteractionHandlers(),
    similarHomesToggleHandlers: createDelegatedClickHandlers('[data-similar-homes-toggle]'),
    similarHomeCardHandlers: createDelegatedClickHandlers('[data-similar-home-card]'),
    seeAllHandlers: createDelegatedClickHandlers('[data-sold-see-all-button]'),
    changeLotHandlers: createDelegatedClickHandlers('[data-lotelevation-change-lot-button]'),
    elevationOptionHandlers: createDelegatedClickHandlers('[data-elevation-option]'),
    elevationSummaryHandlers: createDelegatedClickHandlers('[data-elevation-summary]'),
    zoomInteraction$: new Subject(),
    drawerInteraction$: new Subject(),
    lastActiveChip: null,
    previousFloorPlanId: null,
    previousHousingUnitId: null,
    previousLotId: null,
    // Whether the Floor Plan Detail / Housing Unit Detail view's own Configuration tab is
    // currently selected — that selection is local plugin state elsewhere (floorPlanDetail.plugin.js
    // / housingUnitDetail.plugin.js), so this plugin mirrors it to know when a plan/unit change
    // should re-report "viewing the floor plan" without a fresh tab click. See
    // onDetailTabClicked and the store$ subscription that reacts to it in start().
    isOnFloorPlanConfigTab: false,
    subscriptions: [],
  };

  return {
    name: 'tracking',

    async init(context) {
      context.state.tracking = { started: false, ready: false };
    },

    async start(context) {
      if (context.state.tracking?.started) {
        return;
      }

      const { getState, store$, mapFacade, mapLibraryLoaded$ } = context.services;
      if (!mapFacade) {
        return;
      }

      context.state.tracking = { started: true, ready: false };

      // Chip pill click -> panelUpdatedOccurred$. distinctUntilChanged already satisfies
      // "if the same pill is clicked again, do not fire"; pairwise() never emits on the first
      // (pre-click, default) chip value, matching lotInteraction.plugin.js's own reasoning for
      // the same pairwise() use — see the plan's Global Constraints for the one known gap
      // (auto-reselect fallback in mapChips.plugin.js also flows through this same subscription).
      function onChipSelectionChanged([previous, next]) {
        panelUpdatedOccurred$.next({
          selectedChip: next.chip,
          previousChip: previous.chip,
          resultsCount: next.counts[next.chip] ?? 0,
          cardCount: getActivePanelCardCount(getState()),
          source: 'lot_status_type',
          isMobile: context.state.isMobile,
        });
      }

      // Lot selected on the map -> lotSelectedOccurred$ or mobileLotMapClickedOccurred$.
      function onLotSelected(selectedSitePlanLotId) {
        const currentState = getState();
        const lot = selectLots(currentState).find((candidate) => candidate.sitePlanLotId === selectedSitePlanLotId);
        if (!lot) {
          return;
        }

        const matchingHousePlans = selectFilteredHousePlans(currentState)
          .filter((plan) => plan.lotIds.includes(lot.lotId));

        const planId = matchingHousePlans.length === 1 ? matchingHousePlans[0].planId : null;
        const specId = lot.housingUnitId ?? null;
        const plansCount = lot.housePlanIds.length;

        // Mobile lot card navigation takes precedence over the standard lot selection flow. 
        // If the user is on the mobile lot card view, we emit a mobileLotMapClickedOccurred$ 
        // event and return early.
        if (context.state.isMobile && selectActiveView(currentState) === VIEW_MOBILE_LOT_CARD) {
          mobileLotMapClickedOccurred$.next({
            lot,
            planId,
            specId,
            plansCount,
            isMobile: true,
          });
          return;
        }

        lotSelectedOccurred$.next({
          lot,
          planId,
          specId,
          plansCount,
          isMobile: context.state.isMobile,
        });

        // A lot selection also updates the panel, so it is a panel update too
        // (source: 'lot_selection') — reported in addition to the lot_selected event above.
        // results_count and card_count describe the current panel state — the active status type's
        // lot count (the pill number, e.g. "Available (26)") and the active tab's cards — derived
        // exactly as they are for the pill path: per the spec, "results count is for the
        // lot_status_type_selected" regardless of what triggered the update. selectedChip carries
        // the selected lot's own status.
        panelUpdatedOccurred$.next({
          selectedChip: lot.status,
          previousChip: state.lastActiveChip,
          resultsCount: selectChipLotCounts(currentState)[selectActiveChip(currentState)] ?? 0,
          cardCount: getActivePanelCardCount(currentState),
          source: 'lot_selection',
          isMobile: context.state.isMobile,
        });
        state.lastActiveChip = lot.status;
      }

      // Available chip's selected tab changed -> tabSelectedOccurred$. resultsCount reflects the
      // newly selected tab (getActivePanelCardCount reads selectSelectedTab off currentState,
      // which by the time this fires already reflects the new tab).
      function onTabChanged([previousTab, selectedTab]) {
        const currentState = getState();
        tabSelectedOccurred$.next({
          selectedTab,
          previousTab,
          resultsCount: getActivePanelCardCount(currentState),
          isMobile: context.state.isMobile,
        });
      }

      // Zoom -> mapInteractionOccurred$, debounced so one continuous scroll-wheel/pinch gesture
      // (many rapid zoom_changed ticks) collapses into a single emission once the user pauses,
      // instead of one per tick.
      function onDebouncedZoomInteraction(payload) {
        mapInteractionOccurred$.next({ 
          ...payload, 
          mapType: getTrackingMapType(mapFacade.getMapType?.()) 
        });
      }

      function onZoomInteraction(payload) {
        state.zoomInteraction$.next(payload);
      }

      function onPanInteraction({ inputMethod }) {
        mapInteractionOccurred$.next({
          interactionType: 'pan',
          inputMethod,
          mapType: getTrackingMapType(mapFacade.getMapType?.()),
        });
      }

      function onMapTypeInteraction({ mapType }) {
        mapInteractionOccurred$.next({ 
          interactionType: 'map_type_changed', 
          inputMethod: 'button', 
          mapType 
        });
      }

      function onChipsScrolled() {
        panelScrollOccurred$.next();
      }

      function onCardsScrolled() {
        cardsScrolledOccurred$.next({ isMobile: context.state.isMobile });
      }

      function onNotifyWhenAvailableClicked() {
        notifyWhenAvailableClickedOccurred$.next();
      }

      // Emits floorPlanViewedOccurred$ and emptyConfigViewedOccurred$ (the "No configurations
      // available" placeholder — see its doc comment in redux/observables.js for why that's every
      // time today) for whichever plan/unit is currently active. Shared by two call sites that
      // both mean "the user is now looking at a floor plan image": onDetailTabClicked (switching
      // TO the Configuration tab) and the store$ subscription further below (the
      // active plan/unit changing while the Configuration tab is already selected, e.g. picking a
      // different plan card without leaving that tab).
      //
      // hasConfigurations is hardcoded false (NHS20-9000 UAT fix): components/
      // floorPlanConfiguration.dom.js takes no parameters and always renders the same "No
      // configurations available" placeholder, regardless of whether the active plan/unit's
      // floorplans array has entries — so has_configurations must always report false/blank to
      // match what's actually on screen. A previous version derived this from
      // floorplans.length > 0 (reachability-correct — the Configuration tab is hidden entirely
      // when a plan/unit has no floorplans, see syncFloorPlanConfigTabState in
      // floorPlanDetail.plugin.js/housingUnitDetail.plugin.js — so that condition was always true
      // whenever this code ran) but that tracked the data, not the UI, which is what QA caught:
      // reported true while the visible tab still said "No configurations available." Revisit
      // once floorPlanConfiguration.dom.js renders real configuration content (likely sourced
      // from floorplans[], the same field this used to check).
      function emitFloorPlanConfigViewed(trackingIds) {
        floorPlanViewedOccurred$.next({ ...trackingIds, hasConfigurations: false });
        emptyConfigViewedOccurred$.next(trackingIds);
      }

      // Mobile-only "View floor plan" button clicked -> viewFloorPlanClickedOccurred$.
      // housingUnitDetail.plugin.js reuses the exact same [data-image-expand-button] mobile
      // expand control, on the same shared [data-panel] root this handler binds to once and never
      // re-binds per view — this plugin reports the same tracking event (plan_id populated for
      // one view, spec_id for the other) for both, so this accepts either view rather than only
      // VIEW_FLOOR_PLAN_DETAIL; the guard still exists to reject every OTHER view (listing,
      // lotMapSelection, sold full list).
      function onViewFloorPlanClicked() {
        const currentState = getState();
        const activeView = selectActiveView(currentState);
        if (activeView !== VIEW_FLOOR_PLAN_DETAIL && activeView !== VIEW_HOUSING_UNIT_DETAIL) {
          return;
        }

        viewFloorPlanClickedOccurred$.next(getStaticFloorPlanTrackingIds(currentState));
      }

      function onSimilarHomesToggled(toggleButton) {
        const section = toggleButton.closest('[data-similar-homes-section]');
        if (!section) {
          return;
        }

        // Read AFTER the toggle: plugins/listView.plugin.js's own toggle listener is bound earlier in
        // the plugin start sequence (tracking.plugin runs near the end of index.js's plugins array —
        // see TRACKING_PLUGIN_SEQUENCE.md), so by the time this fires aria-expanded already reflects
        // the new state.
        const expanded = toggleButton.getAttribute('aria-expanded') === 'true';

        // Resolved from the DOM, not getState()/selectSelectedLotId: the Sold listing renders
        // every sold home's card (each with its own Similar Homes section) regardless of what's
        // selected on the map, and this toggle can belong to any one of them — not necessarily
        // whichever lot happens to be selected [data-lot-id] is the sold-card-
        // group wrapper rendered in services/listViewContentService.js#renderSoldHomeCards.
        const soldCardGroup = toggleButton.closest('[data-lot-id]');
        const lotId = soldCardGroup ? Number(soldCardGroup.dataset.lotId) : null;
        if (!lotId || Number.isNaN(lotId)) {
          return;
        }

        similarHomesToggledOccurred$.next({
          expanded,
          lotId,
          similarHomesCount: section.querySelectorAll('[data-similar-home-card]').length,
        });
      }

      function onSimilarHomeCardClicked(cardElement) {
        // Resolved from the DOM for the same reason as onSimilarHomesToggled above — this must be
        // the lot whose Sold Home Card contains the clicked similar-home card, not whichever lot
        // is currently selected on the map.
        const soldCardGroup = cardElement.closest('[data-lot-id]');
        const lotId = soldCardGroup ? Number(soldCardGroup.dataset.lotId) : null;
        if (!lotId || Number.isNaN(lotId)) {
          return;
        }

        const list = cardElement.closest('[data-similar-homes-list]');
        const position = list ? Array.from(list.children).indexOf(cardElement) + 1 : null;

        similarHomeCardClickedOccurred$.next({
          lotId,
          planId: cardElement.dataset.planId ? Number(cardElement.dataset.planId) : null,
          specId: cardElement.dataset.housingUnitId ? Number(cardElement.dataset.housingUnitId) : null,
          position,
        });
      }

      function onSeeAllClicked() {
        soldSeeAllClickedOccurred$.next();
      }

      function onChangeLotClicked() {
        const currentState = getState();
        const lot = selectCurrentDetailLot(currentState);
        const planId = selectActiveFloorPlan(currentState)?.planId ?? null;
        const specId = lot?.housingUnitId ?? null;
        changeLotSelectedOccurred$.next({
          lotId: lot?.lotId ?? null,
          planId,
          specId,
          isMobile: context.state.isMobile,
        });
      }

      function onElevationOptionClicked(optionButton) {
        if (optionButton.getAttribute('aria-pressed') === 'true') {
          return;
        }

        const elevationId = optionButton.dataset.elevationId ?? null;
        // Lead value, not the on-screen label — an untitled elevation reports "Elevation <Display
        // Order>" here (GA4 click_target2 / Segment exterior_name), matching the lead (NHS20-9080).
        const elevationTitle = optionButton.dataset.elevationLeadValue ?? null;
        const list = optionButton.closest('[data-elevation-list]');
        const position = list ? Array.from(list.children).indexOf(optionButton) + 1 : null;
        const isDefault = position === 1;

        const currentState = getState();
        const lot = selectCurrentDetailLot(currentState);
        const planId = selectActiveFloorPlan(currentState)?.planId ?? null;
        const specId = selectActiveHousingUnit(currentState)?.specificationId ?? null;

        exteriorSelectedOccurred$.next({
          lotId: lot?.lotId ?? null,
          planId,
          specId,
          elevationId,
          elevationTitle,
          position,
          isDefault,
          isMobile: context.state.isMobile,
        });
      }

      function onElevationSummaryClicked(summaryElement) {
        const disclosure = summaryElement.closest('[data-elevation-disclosure]');
        if (!disclosure || disclosure.open) {
          return;
        }

        const availableExteriorsCount = disclosure.querySelectorAll('[data-elevation-option]').length;

        const currentState = getState();
        const lot = selectCurrentDetailLot(currentState);
        const planId = selectActiveFloorPlan(currentState)?.planId ?? null;
        const specId = selectActiveHousingUnit(currentState)?.specificationId ?? null;

        exteriorSelectorExpandedOccurred$.next({
          lotId: lot?.lotId ?? null,
          planId,
          specId,
          availableExteriorsCount,
          isMobile: context.state.isMobile,
        });
      }

      // A vertical drag and the drawer opening are reported as the same interaction — both feed the
      // throttled drawerInteraction$ so a drag-to-open doesn't double-count.
      function onDrawerInteraction() {
        state.drawerInteraction$.next();
      }

      function onMapLibraryLoaded() {
        const mapElement = context.root.querySelector('[data-map-element]');
        const chipsElement = context.root.querySelector('.nhs-c-native-interactive-site-map__chips-wrapper');
        // The panel is mounted into the shared layout drawer — a global node, not inside
        // context.root (drawerService.mountElement moves the panel out of it).
        const drawerElement = document.querySelector('[data-drawer]');

        if (mapElement) {
          state.mapInteractionHandlers.bind(mapFacade, mapElement, {
            onZoomInteraction,
            onPanInteraction,
            onMapTypeInteraction,
          });
        }

        // Chip scroll and drawer drag tracking are both mobile-only.
        if (chipsElement && context.state.isMobile) {
          state.chipScrollHandlers.bind(chipsElement, onChipsScrolled);
        }

        const listingContentElement = context.root.querySelector('.nhs-c-native-interactive-site-map__listing-content');

        // Unlike the chip-pill scroll (mobile-only), the card grid can scroll on any breakpoint.
        if (listingContentElement) {
          state.cardsScrollHandlers.bind(listingContentElement, onCardsScrolled);
        }

        const panelElement = context.root.querySelector('[data-panel]');

        if (panelElement) {
          state.getNotifiedInteractionHandlers.bind(panelElement, onNotifyWhenAvailableClicked);
          state.floorPlanDetailInteractionHandlers.bind(panelElement, {
            // Mobile-only per the AC table — the button itself only exists in the DOM on mobile
            // (floorPlanDetail.plugin.js#initializeContent), but this gate keeps that intent
            // explicit here too, matching chipScrollHandlers/drawerDragHandlers below.
            onViewFloorPlanClicked: context.state.isMobile ? onViewFloorPlanClicked : undefined,
          });
          state.similarHomesToggleHandlers.bind(panelElement, onSimilarHomesToggled);
          state.similarHomeCardHandlers.bind(panelElement, onSimilarHomeCardClicked);
          state.elevationOptionHandlers.bind(panelElement, onElevationOptionClicked);
          state.elevationSummaryHandlers.bind(panelElement, onElevationSummaryClicked);
        }

        if (panelElement && context.state.isMobile) {
          state.seeAllHandlers.bind(panelElement, onSeeAllClicked);
          state.changeLotHandlers.bind(panelElement, onChangeLotClicked);
        }

        if (drawerElement && context.state.isMobile) {
          state.drawerDragHandlers.bind(drawerElement, onDrawerInteraction);
          state.drawerOpenHandlers.bind(drawerElement, onDrawerInteraction);
        }

        context.state.tracking.ready = true;
      }

      state.subscriptions.push(
        store$.pipe(
          map((storeState) => ({ chip: selectActiveChip(storeState), counts: selectChipLotCounts(storeState) })),
          distinctUntilChanged((previous, next) => previous.chip === next.chip),
          pairwise(),
        ).subscribe(onChipSelectionChanged)
      );

      state.subscriptions.push(
        store$.pipe(
          map((storeState) => selectSelectedLotId(storeState)),
          distinctUntilChanged(),
          filter((selectedSitePlanLotId) => Boolean(selectedSitePlanLotId)),
        ).subscribe(onLotSelected)
      );

      state.subscriptions.push(
        store$.pipe(
          map((storeState) => selectSelectedTab(storeState)),
          distinctUntilChanged(),
          pairwise(),
          filter(([, next]) => next != null),
        ).subscribe(onTabChanged)
      );

      state.subscriptions.push(
        state.zoomInteraction$.pipe(debounceTime(ZOOM_DEBOUNCE_MS)).subscribe(onDebouncedZoomInteraction)
      );

      state.subscriptions.push(
        state.drawerInteraction$
          .pipe(throttleTime(DRAWER_INTERACTION_THROTTLE_MS))
          .subscribe(() => drawerDraggedOccurred$.next())
      );

      state.subscriptions.push(
        mapLibraryLoaded$.pipe(take(1)).subscribe(onMapLibraryLoaded)
      );

      // Detail view entry -> detailViewOpenedOccurred$. Fires when
      // activeView becomes floorPlanDetail or housingUnitDetail. position comes directly from
      // the navigateTo context's source, set by the map-lot auto-route or card-selection epics.
      function onDetailViewEntered(storeState) {
        const currentLot = selectCurrentDetailLot(storeState);
        const currentFloorPlanId = selectActiveFloorPlanId(storeState);
        const currentHousingUnitId = selectActiveHousingUnitId(storeState);
        const position = selectViewEntrySource(storeState) || null;

        detailViewOpenedOccurred$.next({
          lotId: currentLot?.lotId ?? null,
          planId: currentFloorPlanId ?? null,
          specId: currentHousingUnitId ?? null,
          position,
          isMobile: context.state.isMobile,
        });
      }

      state.subscriptions.push(
        store$.pipe(
          map((storeState) => selectActiveView(storeState)),
          distinctUntilChanged(),
          filter((activeView) => isDetailView(activeView)),
        ).subscribe(() => onDetailViewEntered(getState()))
      );

      // Detail view tab clicked -> detailsTabSelectedOccurred$. Fires when a tab is clicked
      // in floor plan or housing unit detail views, emits with context (lot, plan, spec IDs).
      //
      // Also the single source of truth for the Configuration-tab side effects that a second,
      // independent DOM-delegated tab-click listener used to duplicate (NHS20-9000 UAT fix —
      // that listener sent this same interaction as a second, redundant tracking event).
      // state.isOnFloorPlanConfigTab mirrors the local tab selection so the store$ subscription
      // further below knows whether a later plan/unit change should also count as "viewing the
      // floor plan" (see emitFloorPlanConfigViewed's doc comment above).
      function onDetailTabClicked({ tabValue }) {
        const currentState = getState();
        const activeView = selectActiveView(currentState);

        if (!isDetailView(activeView)) {
          return;
        }

        const lot = selectCurrentDetailLot(currentState);
        const planId = selectActiveFloorPlan(currentState)?.planId ?? null;
        const specId = selectActiveHousingUnit(currentState)?.specificationId ?? null;

        detailsTabSelectedOccurred$.next({
          lotId: lot?.lotId ?? null,
          planId,
          specId,
          selectedTab: tabValue,
          isMobile: context.state.isMobile,
        });

        state.isOnFloorPlanConfigTab = tabValue === TAB_FLOOR_PLAN_CONFIG;

        if (tabValue === TAB_FLOOR_PLAN_CONFIG) {
          emitFloorPlanConfigViewed(getStaticFloorPlanTrackingIds(currentState));
        }
      }

      state.subscriptions.push(
        detailTabClicked$.subscribe(onDetailTabClicked)
      );

      // Filters modal actions -> filter*Occurred$. The modal's open/close/edit state is local to
      // filtersModal.plugin, which pushes each raw action onto filtersModalAction$ from the code
      // that performs it (same shape as detailTabClicked$ above); everything else the events carry
      // is read from the draft here. 'applied' arrives before the draft is applied and 'closed'
      // before it's discarded, so both still see exactly what the user left in the modal.
      function onFiltersModalAction({ action, field }) {
        const currentState = getState();

        switch (action) {
          case 'opened':
            filterPanelOpenedOccurred$.next();
            break;
          case 'closed':
            filterPanelClosedOccurred$.next({
              hadChanges: selectFilterDraftHasPendingChanges(currentState),
            });
            break;
          // Only the fields the tracking plan lists.
          case 'updated':
            if (isTrackedFilterField(field)) {
              filterUpdatedOccurred$.next({ field });
            }
            break;
          case 'applied':
            filtersAppliedOccurred$.next({
              filters: selectFilterDraft(currentState),
              totalFiltersApplied: selectFilterDraftActiveFilterCount(currentState),
              resultsCount: selectFilterDraftResultCount(currentState),
            });
            break;
          case 'cleared':
            filtersClearedOccurred$.next();
            break;
          default:
            break;
        }
      }

      state.subscriptions.push(
        filtersModalAction$.subscribe(onFiltersModalAction)
      );

      // Mobile lot card or back button clicked -> mobileLotCardNavigationOccurred$. Fires when a mobile lot card
      // or back button is clicked, emits with context (lot, plan, spec IDs and optional lot detail properties).
      // clickSource distinguishes between 'card_click' and 'back_button'.
      function onMobileLotNavigationClicked(clickSource = 'card_click') {
        if (!context.state.isMobile) {
          return;
        }

        const currentState = getState();
        const lot = selectCurrentDetailLot(currentState);
        const planId = selectActiveFloorPlan(currentState)?.planId ?? null;
        
        mobileLotCardNavigationOccurred$.next({
          lotId: lot?.lotId ?? null,
          planId,
          lotNumber: lot?.displayLabel ?? null,
          lotGroup: lot?.lotGroupName ?? null,
          lotStatus: lot?.status ?? null,
          plansCount: lot?.housePlanIds?.length ?? 0,
          type: lot?.lotType ?? null,
          size: lot?.formattedLotSize ?? null,
          facing: lot?.lotFacing ?? null,
          clickSource,
          isMobile: true,
        });
      }

      state.subscriptions.push(
        fromEvent(context.root, 'click').subscribe((event) => {
          if (event.target.closest('[data-mobile-lot-card-content]')) {
            onMobileLotNavigationClicked('card_click');
          } else if (event.target.closest('[data-mobile-lot-card-back-button]')) {
            onMobileLotNavigationClicked('back_button');
          }
        })
      );
        
      // Re-emits floorPlanViewedOccurred$/emptyConfigViewedOccurred$ (via
      // emitFloorPlanConfigViewed above) when the active plan/unit changes while
      // state.isOnFloorPlanConfigTab is already true — the click-driven emission in
      // onDetailTabClicked only fires for the tab click itself, so without this,
      // picking a different plan/unit card while already on the Configuration tab (both detail
      // plugins keep the selected tab across an active-item change) would silently undercount.
      // Mapped to a view-tagged key (`plan:<id>` / `unit:<id>`, `null` while elsewhere) the same
      // way the feature's other view-entry tracking does, so a plan and a unit that happen to
      // share a numeric id are never mistaken for "unchanged". Leaving both detail views resets
      // the flag, since a later fresh entry always starts back on Lot & elevation.
      state.subscriptions.push(
        store$.pipe(
          map((storeState) => {
            const activeView = selectActiveView(storeState);
            if (activeView === VIEW_FLOOR_PLAN_DETAIL) {
              const planId = selectActiveFloorPlanId(storeState);
              return planId == null ? null : `plan:${planId}`;
            }
            if (activeView === VIEW_HOUSING_UNIT_DETAIL) {
              const housingUnitId = selectActiveHousingUnitId(storeState);
              return housingUnitId == null ? null : `unit:${housingUnitId}`;
            }
            return null;
          }),
          distinctUntilChanged(),
        ).subscribe((viewKey) => {
          if (viewKey == null) {
            state.isOnFloorPlanConfigTab = false;
            return;
          }
          if (!state.isOnFloorPlanConfigTab) {
            return;
          }
          const currentState = getState();
          emitFloorPlanConfigViewed(getStaticFloorPlanTrackingIds(currentState));
        })
      );
    },

    async stop(context) {
      state.mapInteractionHandlers.unbind();
      state.chipScrollHandlers.unbind();
      state.cardsScrollHandlers.unbind();
      state.getNotifiedInteractionHandlers.unbind();
      state.floorPlanDetailInteractionHandlers.unbind();
      state.similarHomesToggleHandlers.unbind();
      state.similarHomeCardHandlers.unbind();
      state.seeAllHandlers.unbind();
      state.changeLotHandlers.unbind();
      state.elevationOptionHandlers.unbind();
      state.elevationSummaryHandlers.unbind();
      state.drawerDragHandlers.unbind();
      state.drawerOpenHandlers.unbind();
      state.subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
      state.subscriptions = [];
      state.isOnFloorPlanConfigTab = false;
      context.state.tracking = { started: false, ready: false };
    },
  };
}
